import { Box, Href, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsCustomFieldsPoliciesFragment,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesReferenceContractFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import LabeledFancyCheckbox from 'modules/core/components/Inputs/LabeledFancyCheckbox'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getFirstError } from 'modules/contracts/lib/errors'
import { translate } from 'modules/contracts/lib/translate'

import SpainLegalAndExtras from './SpainLegalAndExtras'
import { Authentication as A3innuvaAuthentication } from './SpainLegalAndExtras/A3innuvaFields'
import ContractTypeSelect from './_ContractTypeSelect'
import CustomFields from './_CustomFields'
import ForfaitJourLegalAndExtras from './_ForfaitJourLegalAndExtras'
import FranceLegalAndExtras from './_FranceLegalAndExtras'
import GermanyLegalAndExtras from './_GermanyLegalAndExtras'
import { stepHasErrors } from './validator'

interface ContractTypeHintProps {
  legalEntityId: number
  step: number
  type: ContractFlowType
}

const ContractTypeHint = ({ legalEntityId, step, type }: ContractTypeHintProps) => {
  const { navigation } = useNavigation()

  const trackLink = () => {
    track(trackingEvents.employee.contracts.goToContractType, {
      step: step,
      type: type,
    })
  }

  return (
    <Hint color="grey200">
      <Href
        onClick={() => trackLink()}
        href={navigation.settings.legalEntitiesId.contracts.fullPath({ id: legalEntityId })}
        target="_blank"
      >
        <Text color="grey1000" weight="normal" size="100" underlined>
          {i18n.t('contract_versions.flows.fix.steps.legal_and_extras.hints.contract_type')}
          <Box paddingLeft="s4" style={{ display: 'inline' }}>
            <Text color="grey1000" weight="normal" size="100" displayContents>
              {i18n.t(
                'contract_versions.flows.fix.steps.legal_and_extras.hints.contract_type_link'
              )}
            </Text>
          </Box>
        </Text>
      </Href>
    </Hint>
  )
}

interface Props {
  form: Form
  translations: { [key: string]: string }
  step: number
  type: ContractFlowType
  setIsCurrentStepCompleted: (isCompleted: boolean) => void
  canSeeContractType: boolean
  canSeeTrialPeriod: boolean
  canSeeTrialEndDate: boolean
  canSeeWorkingDayType: boolean
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  employee: ContractsEmployeesEmployeeFragment | null | undefined
  contractVersion:
    | ContractsEmployeesReferenceContractFragment
    | ContractsEmployeesContractVersionFragment
    | null
    | undefined
  customFieldsContextlessPolicies: ContractsCustomFieldsPoliciesFragment | undefined
}

const LegalAndExtras = ({
  form,
  translations,
  step,
  type,
  setIsCurrentStepCompleted,
  canSeeContractType,
  canSeeTrialPeriod,
  canSeeTrialEndDate,
  canSeeWorkingDayType,
  hasDevTimeTrackingWorkableDaysDefinition,
  employee: graphqlEmployee,
  contractVersion: graphqlContractVersion,
  customFieldsContextlessPolicies,
}: Props) => {
  const {
    compensations,
    initialCompensations,
    additionalCompensations,
    initialAdditionalCompensations,
  } = useContractFlow()
  const endsOnField = form.get('ends_on')
  const trialPeriodEndField = form.has('trial_period_ends_on')
    ? form.get('trial_period_ends_on')
    : undefined
  const trialPeriodEndValue = trialPeriodEndField ? trialPeriodEndField.value : ''
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setIsCurrentStepCompleted(
      !stepHasErrors(
        form,
        graphqlContractVersion?.startsOn,
        compensations,
        initialCompensations,
        graphqlContractVersion?.payrollPolicy?.policy?.id,
        additionalCompensations,
        initialAdditionalCompensations
      )
    )
  }, [
    endsOnField.value,
    trialPeriodEndValue,
    form.dirtyFieldsKeys.join(),
    compensations?.length,
    initialCompensations?.length,
    additionalCompensations?.length,
    initialAdditionalCompensations?.length,
  ])
  /* eslint-enable react-hooks/exhaustive-deps */

  const trialPeriodOptions = [
    {
      value: 1,
      label: i18n.t('contract_versions.forms.basic_information.yes'),
    },
    {
      value: 0,
      label: i18n.t('contract_versions.forms.basic_information.no'),
    },
  ]

  const trialPeriodName = () => {
    const currentOption = trialPeriodOptions.find(
      (e) => !!e.value === form.get('has_trial_period').value
    )
    return currentOption?.label || `-- ${i18n.t('common.none')} --`
  }

  const legalEntity = graphqlEmployee?.legalEntity
  const isSpanish =
    graphqlContractVersion && type === 'fix'
      ? graphqlContractVersion?.country === 'es'
      : legalEntity?.country === 'es'
  const isFrench =
    graphqlContractVersion && type === 'fix'
      ? graphqlContractVersion?.country === 'fr'
      : legalEntity?.country === 'fr'
  const isItalian = graphqlContractVersion && type === 'fix' && legalEntity?.country === 'it'
  const isGerman =
    graphqlContractVersion && type === 'fix'
      ? graphqlContractVersion?.country === 'de'
      : legalEntity?.country === 'de'
  const isPortuguese =
    graphqlContractVersion && type === 'fix'
      ? graphqlContractVersion?.country === 'pt'
      : legalEntity?.country === 'pt'
  const hasContractType = isSpanish || isFrench || isGerman || isPortuguese
  const hasTrialPeriod = form.has('has_trial_period')

  const legalEntityCountry = legalEntity?.country
  const isForfaitJourEntity = (isFrench && legalEntityCountry === 'fr') || isItalian

  return (
    <Box gap="s32" paddingBottom="s40">
      <Box gap="s8">
        <FormWrapper>
          <Text size="400" weight="medium" color="grey1000">
            {translate(translations, 'header')}
          </Text>
        </FormWrapper>
      </Box>
      {legalEntityCountry === 'es' && isSpanish && legalEntity?.company?.id && (
        <A3innuvaAuthentication companyId={legalEntity?.company?.id} />
      )}
      <Box gap="s24">
        {!hasDevTimeTrackingWorkableDaysDefinition &&
          canSeeContractType &&
          hasContractType &&
          legalEntity?.id && (
            <FormWrapper
              hint={<ContractTypeHint legalEntityId={legalEntity.id} step={step} type={type} />}
            >
              <ContractTypeSelect
                form={form}
                type={type}
                contractVersionCountry={graphqlContractVersion?.country ?? undefined}
                legalEntityCountry={legalEntityCountry}
              />
            </FormWrapper>
          )}
        {legalEntityCountry === 'es' && isSpanish && (
          <FormWrapper>
            <LabeledFancyCheckbox
              field={form.get('es_has_teleworking_contract')}
              label={translate(translations, 'teleworking') || ''}
            />
          </FormWrapper>
        )}
        {form.has('ends_on') && (
          <FormWrapper>
            <DayPicker
              withNull
              value={endsOnField.value}
              error={getFirstError(endsOnField)}
              onChange={(value) => endsOnField.set(value)}
              label={translate(translations, 'end_date')}
              minDate={stringToDate(form.get('effective_on').value, 'ui')}
              withNullLabel={translate(translations, 'end_date_none')}
            />
          </FormWrapper>
        )}
        {hasTrialPeriod && canSeeTrialPeriod && (
          <FormWrapper>
            <Box gap="s24">
              <DropdownSearchBar
                name={trialPeriodName()}
                label={translate(translations, 'trial_period') || ''}
                onSelect={(item) => {
                  form.get('has_trial_period').set(!!item.value)
                }}
                labelNotFound={i18n.t('contract_versions.forms.basic_information.option_not_found')}
                data={trialPeriodOptions}
                searchLabel={i18n.t(
                  'contract_versions.forms.contract_extra_information.a3innuva.search_label'
                )}
                popoverWidth="s400"
              />
              {canSeeTrialEndDate && form.get('has_trial_period').value && (
                <DayPicker
                  value={trialPeriodEndField?.value}
                  error={getFirstError(trialPeriodEndField)}
                  onChange={(value) => trialPeriodEndField?.set(value)}
                  label={translate(translations, 'trial_period_end')}
                  minDate={stringToDate(form.get('effective_on').value, 'ui')}
                />
              )}
            </Box>
          </FormWrapper>
        )}
        {legalEntityCountry === 'es' && isSpanish && legalEntity?.id && (
          <SpainLegalAndExtras
            form={form}
            translations={translations}
            legalEntityId={legalEntity?.id}
          />
        )}
        {legalEntityCountry === 'fr' && isFrench && (
          <FranceLegalAndExtras
            form={form}
            translations={translations}
            canSeeWorkingDayType={canSeeWorkingDayType}
          />
        )}
        {isForfaitJourEntity && (
          <ForfaitJourLegalAndExtras form={form} translations={translations} />
        )}
        {legalEntityCountry === 'de' && isGerman && (
          <GermanyLegalAndExtras form={form} translations={translations} />
        )}
        {legalEntityCountry === 'es' && isSpanish && (
          <FormWrapper>
            <TextArea
              field={form.get('es_contract_observations')}
              label={translate(translations, 'contract_observations')}
            />
          </FormWrapper>
        )}
        {legalEntity?.id && (
          <CustomFields
            form={form}
            translations={translations}
            step={step}
            type={type}
            legalEntityId={legalEntity.id}
            customFieldsContextlessPolicies={customFieldsContextlessPolicies}
          />
        )}
      </Box>
    </Box>
  )
}

export default observer(LegalAndExtras)
